import { useCustom, useTranslate, useGetLocale } from "@refinedev/core";
import { FormCard, SectionHeader, StatusBadge, type Column } from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

interface CredentialSummary {
  id: string;
  clientId: string;
  secretPrefix: string;
  createdAt?: string;
  lastUsedAt?: string;
  expiresAt?: string;
  removedAt?: string;
}

interface AllowedIpSummary {
  id: string;
  cidr: string;
  createdAt?: string;
}

interface SelfAccount {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  credentials: CredentialSummary[];
  allowedIps: AllowedIpSummary[];
}

// jve-5-2: self-service (row-level) view — the operator sees their OWN account only. Bound to
// GET /api/service-accounts/me (identity derived from the token subject — no id input, so another
// operator's row is unaddressable). Identity + credentials + allowlist are read-only; the
// generate/delete-own-credential actions from the Jmix self-service view need the self-scoped
// credential write endpoints, which are part of the not-yet-ported admin/credential surface
// (documented parity gap, see the 5.3 checklist).
//
// jve-2-6: visual parity with the deleted Jmix self-service view — the identity block mirrors the
// Jmix `formLayout` (1→2-column responsive read-only fields, labels above values), and the
// credentials grid keeps the Jmix column set (clientId / secretPrefix / created / last used /
// expires). Composed from the @fawkes/ui FormCard + SectionHeader + DataTable vocabulary.
export function ServiceAccountSelfService() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "en";
  const { data, isLoading, isError } = useCustom<SelfAccount>({
    url: "service-accounts/me",
    method: "get",
  });

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !data?.data) {
    return <p className="fawkes-error">{t("serviceAccountDetailView.loadFailed")}</p>;
  }

  const me = data.data;

  const credentialColumns: Column<CredentialSummary>[] = [
    { header: t("serviceAccountSelfServiceView.credentials.column.clientId"), cell: (c) => c.clientId },
    { header: t("serviceAccountSelfServiceView.credentials.column.secretPrefix"), cell: (c) => c.secretPrefix },
    { header: t("serviceAccountSelfServiceView.credentials.column.createdAt"), cell: (c) => formatDateTime(c.createdAt, locale) },
    { header: t("serviceAccountSelfServiceView.credentials.column.lastUsedAt"), cell: (c) => formatDateTime(c.lastUsedAt, locale) },
    { header: t("serviceAccountSelfServiceView.credentials.column.expiresAt"), cell: (c) => formatDateTime(c.expiresAt, locale) },
  ];

  const allowlistColumns: Column<AllowedIpSummary>[] = [
    { header: t("serviceAccountSelfServiceView.allowlist.column.cidr"), cell: (ip) => ip.cidr },
    { header: t("serviceAccountSelfServiceView.allowlist.column.createdAt"), cell: (ip) => formatDateTime(ip.createdAt, locale) },
  ];

  return (
    <FormCard>
      <SectionHeader>{me.name}</SectionHeader>
      <p className="fawkes-muted fawkes-form-intro">{t("serviceAccountSelfServiceView.intro")}</p>

      <SectionHeader>{t("serviceAccountSelfServiceView.identity.title")}</SectionHeader>
      <dl className="fawkes-field-grid">
        <div className="fawkes-field-grid__item">
          <dt className="fawkes-field-grid__label">{t("serviceAccountSelfServiceView.identity.username")}</dt>
          <dd className="fawkes-field-grid__value">{me.name}</dd>
        </div>
        <div className="fawkes-field-grid__item">
          <dt className="fawkes-field-grid__label">{t("serviceAccountSelfServiceView.identity.active")}</dt>
          <dd className="fawkes-field-grid__value">
            <StatusBadge tone={me.active ? "success" : "neutral"}>
              {me.active
                ? t("serviceAccountListView.status.active")
                : t("serviceAccountListView.status.disabled")}
            </StatusBadge>
          </dd>
        </div>
        <div className="fawkes-field-grid__item">
          <dt className="fawkes-field-grid__label">{t("serviceAccountSelfServiceView.identity.createdDate")}</dt>
          <dd className="fawkes-field-grid__value">{formatDateTime(me.createdAt, locale)}</dd>
        </div>
      </dl>

      <SectionHeader>{t("serviceAccountSelfServiceView.credentials.title")}</SectionHeader>
      <p className="fawkes-muted fawkes-section-intro">{t("serviceAccountSelfServiceView.credentials.intro")}</p>
      <IndeelbareTabel tabelKey="service-account-self-service-toegestane-ips"
        columns={credentialColumns}
        rows={(me.credentials ?? []).filter((c) => !c.removedAt)}
        getRowKey={(c) => c.id}
        empty={t("serviceAccountDetailView.credentials.none")}
      />

      <SectionHeader>{t("serviceAccountSelfServiceView.allowlist.title")}</SectionHeader>
      <p className="fawkes-muted fawkes-section-intro">{t("serviceAccountSelfServiceView.allowlist.intro")}</p>
      <IndeelbareTabel tabelKey="service-account-self-service-weigeringen"
        columns={allowlistColumns}
        rows={me.allowedIps ?? []}
        getRowKey={(ip) => ip.id}
        empty={t("serviceAccountDetailView.allowlist.none")}
      />
    </FormCard>
  );
}
